import { create } from "zustand";
import { api } from "../api";
import { getActiveOrg, setActiveOrgId } from "../lib/activeOrg";
import { queryClient } from "../lib/queryClient";
import { useAuthStore } from "./authStore";
import { useUiStore } from "./uiStore";
import { useNewFieldPrompt } from "./newFieldPrompt";
import { useJobs } from "./jobBus";
import { useMergeWatch } from "./mergeWatchStore";
import { useBoundaryOverlap } from "./boundaryOverlap";
import type { OrgMembership } from "../types/contract";

// One organization is on screen. Its list queries are keyed by that org, and
// a switch still drops the previous org's cache and closes its surfaces.
interface OrgState {
  orgs: OrgMembership[];
  activeOrgId: string | null;
  loading: boolean;
  hydrated: boolean; // a first org-list load has completed (success or fail) — gates the zero-org screen
  error: string | null;
  activeOrg: () => OrgMembership | null;
  hydrate: () => Promise<void>; // on login/bootstrap: load orgs + choose the active one
  refresh: () => Promise<void>; // re-load orgs after a membership change, keeping the active one
  switchOrg: (orgId: string) => void; // user picked a different org
  adopt: (orgs: OrgMembership[], activeId?: string) => void; // after join/leave/create
  reset: () => void; // logout
}

function purgeForSwitch() {
  queryClient.clear(); // one org at a time → the prior org's fields/layers/etc. must not linger
  useUiStore.getState().clearFieldScope(); // selection / open map / work-orders / rename — all org-scoped
  useNewFieldPrompt.getState().dismiss();
  useJobs.getState().reset(); // the prior org's job toasts must not carry over (WS re-scopes via useJobTracker)
  // A merge belongs to ONE org and its strip names that org's fields — carrying it across a
  // switch would show one customer's work on another's dashboard. The merge itself is unaffected.
  useMergeWatch.getState().reset();
  // An add-field overlap question is org-scoped: answering it after a switch would apply the
  // verdict (and any delete) in the WRONG org, and leaving it pending hangs the create forever.
  useBoundaryOverlap.getState().reset();
}

// The session's token carries the AUTHORITATIVE membership set (id + role — Front-End.md §A7): it's what
// the backend validates active-org scoping against, so if the token says the user belongs to orgs, they do.
// The rich list (names, join codes) is enrichment from GET /orgs. Seed from the token so a reload is usable
// the instant the session is restored — with a placeholder name until enrichment lands.
function seedFromSession(): OrgMembership[] {
  const claim = useAuthStore.getState().session?.orgs ?? [];
  return claim.map((o) => ({
    org_id: o.id,
    name: `Organization ${o.id.slice(0, 6)}`,
    role: o.role,
  }));
}

export const useOrgStore = create<OrgState>((set, get) => ({
  orgs: [],
  activeOrgId: getActiveOrg(),
  loading: false,
  hydrated: false,
  error: null,
  activeOrg: () =>
    get().orgs.find((o) => o.org_id === get().activeOrgId) ?? null,

  async hydrate() {
    const seeded = seedFromSession();
    // Login and reload both land here with the module org still null. A list
    // read that started in that window is the other org's fields. Drop it
    // the same way a switch does, including the null → org step.
    const place = (active: string | null, patch: Partial<OrgState>) => {
      const prev = get().activeOrgId;
      setActiveOrgId(active);
      set({ ...patch, activeOrgId: active });
      if (active !== prev) purgeForSwitch();
    };
    if (seeded.length) {
      const persisted = get().activeOrgId;
      const active = seeded.some((o) => o.org_id === persisted)
        ? persisted
        : seeded[0].org_id;
      place(active, {
        orgs: seeded,
        hydrated: true,
        loading: true,
        error: null,
      });
    } else {
      set({ loading: true, error: null });
    }
    try {
      const orgs = await api.listMyOrgs();
      // Trust the token over an empty rich list: if GET /orgs comes back empty but the session says we
      // have orgs, KEEP the seed — a backend inconsistency must not strand the user on the zero-org screen.
      if (orgs.length === 0 && seeded.length > 0) {
        set({ loading: false, hydrated: true });
        return;
      }
      const persisted = get().activeOrgId;
      const active = orgs.some((o) => o.org_id === persisted)
        ? persisted
        : (orgs[0]?.org_id ?? null);
      place(active, { orgs, loading: false, hydrated: true });
    } catch (e) {
      // Transient failure: stay usable on the token-seeded list if we have one; otherwise surface retry.
      set({
        loading: false,
        hydrated: true,
        error: seeded.length
          ? null
          : e instanceof Error
            ? e.message
            : "Could not load your organizations.",
      });
    }
  },

  async refresh() {
    try {
      get().adopt(await api.listMyOrgs()); // keep the current active org if still a member
    } catch {
      /* transient — keep the current list */
    }
  },

  switchOrg(orgId) {
    if (
      orgId === get().activeOrgId ||
      !get().orgs.some((o) => o.org_id === orgId)
    )
      return;
    setActiveOrgId(orgId);
    set({ activeOrgId: orgId });
    purgeForSwitch();
  },

  adopt(orgs, activeId) {
    const prev = get().activeOrgId;
    const want =
      activeId && orgs.some((o) => o.org_id === activeId)
        ? activeId
        : orgs.some((o) => o.org_id === prev)
          ? prev
          : (orgs[0]?.org_id ?? null);
    setActiveOrgId(want);
    set({ orgs, activeOrgId: want });
    if (want !== prev) purgeForSwitch();
  },

  reset() {
    setActiveOrgId(null);
    set({
      orgs: [],
      activeOrgId: null,
      loading: false,
      hydrated: false,
      error: null,
    });
    queryClient.clear();
    useUiStore.getState().clearFieldScope(); // logout: don't leak field selection to the next user
    useNewFieldPrompt.getState().dismiss();
    useJobs.getState().reset(); // logout: don't leak the prior user's job toasts
    useMergeWatch.getState().reset(); // same, for a merge in flight
    useBoundaryOverlap.getState().reset(); // logout: never strand a pending create behind an unanswerable dialog
  },
}));
